import { Archive, ChevronLeft, ChevronRight, FolderTree, Plus, RefreshCw, Search, X } from 'lucide-react'

import { QueryState } from '@krp/sale-ui'
import { Button, CheckboxField, InfoTip, SelectField, TextField } from '@krp/ui-kit'

import type { OwnProductsActions, OwnProductsVm } from '../model/types'
import './own-nomenclature.css'
import { ICON_SIZE, ICON_SIZE_SMALL } from '../../../ownIcons'

/**
 * «Номенклатура» → вкладка своей базы: товары страницами по наименованию, отбор по артикулу или штрихкоду целиком, по группе
 * и с архивом. Поиска по части названия здесь нет: текстовый поиск товаров — только единый (общая «Номенклатура», CLAUDE.md §8).
 * Кнопки записи видны всегда; без права менять товары они выключены, причина написана рядом. Экран получает vm и actions.
 */
export function OwnProductsScreen({ vm, actions }: { vm: OwnProductsVm; actions: OwnProductsActions }) {
  const write = vm.writeDisabledReason === null
  return (
    <section className="own-screen" aria-labelledby="own-products-title">
      <header className="own-toolbar">
        <h2 id="own-products-title">Товары своей базы</h2>
        <Button variant="primary" icon={<Plus size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={actions.create}>
          Новый товар
        </Button>
        <Button variant="ghost" icon={<FolderTree size={ICON_SIZE} aria-hidden="true" />} onClick={actions.openGroups}>
          Группы
        </Button>
        <Button variant="ghost" icon={<RefreshCw size={ICON_SIZE} aria-hidden="true" />} busy={vm.query.refreshing} onClick={actions.reload}>
          Обновить
        </Button>
      </header>
      {vm.writeDisabledReason !== null && (
        <p className="own-muted" role="note">
          Создание и правка выключены. {vm.writeDisabledReason}
        </p>
      )}

      <div className="own-section" role="search" aria-label="Отбор товаров">
        <div className="own-row">
          <TextField label="Артикул целиком" value={vm.filter.sku} onChange={(sku) => actions.filterChanged({ ...vm.filter, sku })} />
          <TextField label="Штрихкод целиком" value={vm.filter.barcode} onChange={(barcode) => actions.filterChanged({ ...vm.filter, barcode })} />
          <SelectField
            label="Группа"
            value={vm.filter.groupId}
            options={vm.groupOptions}
            onChange={(groupId) => actions.filterChanged({ ...vm.filter, groupId })}
          />
        </div>
        <div className="own-row">
          <CheckboxField
            label="Показывать архив"
            checked={vm.filter.showArchived}
            onChange={(showArchived) => actions.filterChanged({ ...vm.filter, showArchived })}
          />
          <InfoTip label="Поиск по названию">
            Здесь отбор точный: артикул или штрихкод целиком. Найти товар по части названия — в общей «Номенклатуре»: там единый
            поиск по всем базам.
          </InfoTip>
          <Button variant="primary" icon={<Search size={ICON_SIZE} aria-hidden="true" />} onClick={actions.apply}>
            Показать
          </Button>
          <Button variant="ghost" icon={<X size={ICON_SIZE} aria-hidden="true" />} onClick={actions.reset}>
            Сбросить отбор
          </Button>
        </div>
      </div>

      <p className="own-muted" aria-live="polite">
        {vm.appliedText}
        {vm.query.status === 'ready' ? ` · страница ${vm.pageNumber}, строк ${vm.rows.length}` : ''}
      </p>

      <QueryState query={vm.query} emptyText="Таких товаров в своей базе нет" onRetry={actions.reload} what="Товары" />

      {vm.rows.length > 0 && (
        <ul className="own-list" aria-label="Товары своей базы">
          {vm.rows.map((row) => (
            <li key={row.id} data-archived={row.archived}>
              <Button variant="ghost" onClick={() => actions.open(row.id)}>
                {row.name}
              </Button>
              {row.archived && (
                <span className="own-badge">
                  <Archive size={ICON_SIZE_SMALL} aria-hidden="true" />в архиве
                </span>
              )}
              <span className="own-list__meta">
                {row.skuText} · {row.barcodesText} · {row.unitText}
              </span>
              <span className="own-list__meta">
                {row.groupText} · {row.vatText}
              </span>
            </li>
          ))}
        </ul>
      )}

      {(vm.hasPrevious || vm.hasNext) && (
        <nav className="own-row" aria-label="Страницы">
          <Button icon={<ChevronLeft size={ICON_SIZE} aria-hidden="true" />} disabled={!vm.hasPrevious} onClick={actions.previous}>
            Назад
          </Button>
          <span>Страница {vm.pageNumber}</span>
          <Button icon={<ChevronRight size={ICON_SIZE} aria-hidden="true" />} disabled={!vm.hasNext} onClick={actions.next}>
            Дальше
          </Button>
        </nav>
      )}
    </section>
  )
}
